import { useRef, useState } from "react";
import { Upload, Loader2, FileText, BookPlus } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API } from "../lib/api";

export default function UploadDialog({ open, onOpenChange }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const navigate = useNavigate();

  const pickFile = (f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".txt") && !name.endsWith(".epub")) {
      toast.error("only .txt and .epub files are supported");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("file too large (max 20 mb)");
      return;
    }
    setFile(f);
    // guess title from filename
    if (!title) setTitle(f.name.replace(/\.(txt|epub)$/i, "").replace(/[._-]+/g, " "));
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) pickFile(e.dataTransfer.files[0]);
  };

  const upload = async () => {
    if (!file) { toast.error("choose a file first"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (title) form.append("title", title);
      if (author) form.append("author", author);
      const token = localStorage.getItem("pba_token");
      const res = await fetch(`${API}/books/upload`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `upload failed (${res.status})`);
      }
      const data = await res.json();
      toast.success("book added to your shelf.");
      onOpenChange(false);
      setFile(null); setTitle(""); setAuthor("");
      navigate(`/book/${data.book.id}`);
    } catch (e) {
      toast.error(e.message || "upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl lowercase tracking-tight">bring your own book.</DialogTitle>
          <DialogDescription className="font-ui text-sm text-muted-foreground">
            drop in a .txt or .epub you legally own. it stays private to your shelf and gets all the same tools —
            audiobook, notes, study mode.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="upload-drop-zone"
          className="border-2 border-dashed border-border rounded-md p-8 text-center hover:bg-secondary/50 transition-colors cursor-pointer"
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-6 h-6 text-primary" />
              <div className="text-left">
                <div className="font-display text-base">{file.name}</div>
                <div className="font-ui text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} kb</div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-7 h-7 text-primary" />
              <div className="font-ui text-sm">drop a file here, or <span className="text-primary underline underline-offset-2">choose one</span></div>
              <div className="font-ui text-[10px] tracking-caps text-muted-foreground">.txt or .epub · up to 20 mb</div>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".txt,.epub"
            data-testid="upload-file-input"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
          />
        </div>

        <div className="space-y-3">
          <div>
            <label className="font-ui text-xs tracking-caps text-muted-foreground">title (optional)</label>
            <Input
              data-testid="upload-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="in the time of the butterflies"
              className="mt-1"
            />
          </div>
          <div>
            <label className="font-ui text-xs tracking-caps text-muted-foreground">author (optional)</label>
            <Input
              data-testid="upload-author-input"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="julia alvarez"
              className="mt-1"
            />
          </div>
        </div>

        <Button
          onClick={upload}
          disabled={busy || !file}
          data-testid="upload-submit-btn"
          className="w-full rounded-full font-ui"
        >
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> parsing…</> : <><BookPlus className="w-4 h-4 mr-2" /> add to my shelf</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
